import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import FacebookIcon from '@mui/icons-material/Facebook';

import GoogleIcon from '../components/GoogleIcon';
import Navbar from '../components/navbar';
import { useAppState } from '../reduxish/appState';
import { DEFAULT_CLIENT_CONFIG } from '../api/configApi';
import IClientConfig from '../models/IClientConfig';

function buildPasswordHint(c: IClientConfig): string {
    const reqs: string[] = [];
    if (c.passwordRequireUpper) reqs.push('uppercase');
    if (c.passwordRequireLower) reqs.push('lowercase');
    if (c.passwordRequireDigit) reqs.push('number');
    if (c.passwordRequireSpecial) reqs.push('special character');
    const reqText = reqs.length > 0 ? ` with ${reqs.join(', ')}` : '';
    return `At least ${c.passwordMinLength} characters${reqText}`;
}


export default function SignupPage() {
    const [displayName, setDisplayName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const { state } = useAppState();
    const { signUpUserWithEmailAndPassword, signInWithGoogle, signInWithFacebook } = state.auth!;
    const clientConfig = state.clientConfig ?? DEFAULT_CLIENT_CONFIG;
    const navigate = useNavigate();

    const handleEmailSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!email.trim() || !password.trim() || !displayName.trim()) {
            setError('Please fill in all required fields.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (password.length < clientConfig.passwordMinLength) {
            setError(`Password must be at least ${clientConfig.passwordMinLength} characters long.`);
            return;
        }

        setLoading(true);
        try {
            await signUpUserWithEmailAndPassword(email, password, displayName);
            setSuccess('Account created! Please check your email to verify your account, then sign in.');
            setTimeout(() => navigate('/signin'), 3000);
        } catch (err: any) {
            setError(err.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignup = async () => {
        setError('');
        try {
            await signInWithGoogle();
        } catch (err: any) {
            setError(err.message || 'Google sign up failed.');
        }
    };

    const handleFacebookSignup = async () => {
        setError('');
        try {
            await signInWithFacebook();
        } catch (err: any) {
            setError(err.message || 'Facebook sign up failed.');
        }
    };

    return (
        <>
            <Navbar />
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center' }}>
                        Create Account
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleEmailSignup} noValidate>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="displayName"
                            label="Display Name"
                            name="displayName"
                            autoComplete="name"
                            autoFocus
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            disabled={loading}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            helperText={buildPasswordHint(clientConfig)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirmPassword"
                            label="Confirm Password"
                            type="password"
                            id="confirmPassword"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={loading}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 2, mb: 1, py: 1.5 }}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Sign Up'}
                        </Button>
                    </Box>

                    <Divider sx={{ my: 3 }}>or</Divider>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="google"
                            startIcon={<GoogleIcon />}
                            onClick={handleGoogleSignup}
                            sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
                        >
                            Sign Up with Google
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            color="facebook"
                            startIcon={<FacebookIcon />}
                            onClick={handleFacebookSignup}
                            sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
                        >
                            Sign Up with Facebook
                        </Button>
                    </Box>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2">
                            Already have an account?{' '}
                            <Link to="/signin" style={{ color: 'inherit' }}>
                                Sign In
                            </Link>
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </>
    );
}
